// Enums
export type ActionLibraryInstanceCriticality = 'high' | 'medium' | 'low';
export type ActionLibraryInstanceStatus = 'completed' | 'in_progress' | 'not_started';
export type ActionLibraryInstanceStepStatus = 'not_started' | 'in_progress' | 'completed' | 'not_applicable';

// Shared Generics
export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

// Area/SubArea from AssetActionDetail (schema Area, SubArea)
export type InstanceAreaDataDTO = {
  id: number;
  name: string;
  name_translation_key: string;
  code: string;
  display_name: string;
  display_name_translation_key: string;
  slug: string;
  is_active: boolean;
};

export type InstanceSubAreaDataDTO = {
  id: number;
  name: string;
  name_translation_key: string;
  area: InstanceAreaDataDTO;
  slug: string;
  is_active: boolean;
};

export type InstanceAreaData = {
  id: number;
  name: string;
  nameTranslationKey: string;
  code: string;
  displayName: string;
  displayNameTranslationKey: string;
  slug: string;
  isActive: boolean;
};

export type InstanceSubAreaData = {
  id: number;
  name: string;
  nameTranslationKey: string;
  slug: string;
  isActive: boolean;
  area?: InstanceAreaData;
};

// DTO (snake_case) – matches API response
export type ActionLibraryInstanceStepDTO = {
  id: number;
  title: string;
  instruction: string;
  mandatory: boolean;
  mandatory_tooltip?: string | null;
  order: number;
  is_completed?: boolean;
  completed_at?: string | null;
  completed_by?: number | null;
  status?: string;
  status_display?: string;
  block_reason?: string;
  completion_comment?: string;
  reference?: string | null;
  relevant_for?: string | null;
  reported_to?: string | null;
  tags?: Record<string, unknown> | null;
  assigned_to?: number | null;
  assigned_to_full_name?: string | null;
  comments?: unknown[];
};

export type ActionLibraryInstanceDTO = {
  id: number;
  title: string;
  description: string;
  special_instruction: string;
  reported_to?: string;
  area?: number | null;
  area_display?: string;
  area_data?: InstanceAreaDataDTO | null;
  sub_area?: number | null;
  sub_area_display?: string;
  sub_area_data?: InstanceSubAreaDataDTO | null;
  criticality: ActionLibraryInstanceCriticality;
  criticality_display?: string;
  deadline: string;
  reminder: string | null;
  status: ActionLibraryInstanceStatus;
  status_display?: string;
  status_percent: number;
  organization: number;
  asset: number;
  asset_name: string;
  asset_type: string;
  asset_importance?: string | null;
  asset_importance_display?: string | null;
  owner: number;
  owner_full_name: string;
  delegated_to: number | null;
  delegated_to_full_name: string;
  delegation_comment?: string | null;
  template?: number | null;
  template_id?: number | null;
  template_name?: string | null;
  template_summary?: string | null;
  template_reference?: string | null;
  template_source?: string | null;
  template_source_display?: string | null;
  template_tags?: string | null;
  validation_period?: string | null;
  result_validity_period_months?: number | null;
  validity_period_months?: number | null;
  is_completed?: string | boolean;
  completed_steps_count?: string | number;
  total_steps_count?: string | number;
  steps_count?: number;
  can_complete?: string | boolean;
  complete_button_tooltip?: string | null;
  can_reopen?: string | boolean;
  can_delegate?: string | boolean;
  can_delete?: boolean;
  created_at: string;
  updated_at: string;
  completion_comment?: string | null;
  valid_until?: string | null;
  reference?: string | null;
  tags?: Record<string, unknown> | null;
  steps?: ActionLibraryInstanceStepDTO[];
  instance_comments?: unknown[];
  instance_comments_count?: number;
  last_instance_comment?: InstanceCommentDTO | null;
  status_logs?: unknown[];
};

// Bulk Create DTOs
export type BulkCreateActionLibraryInstanceAdhocStepDTO = {
  title: string;
  instruction: string;
  mandatory: boolean;
  order: number;
  reference?: string;
  relevant_for?: string;
};

export type BulkCreateActionLibraryInstanceAdhocDataDTO = {
  title: string;
  description: string;
  special_instruction: string;
  steps: BulkCreateActionLibraryInstanceAdhocStepDTO[];
};

export type BulkCreateActionLibraryInstanceTimeParametersDTO = {
  deadline: string;
  reminder?: string | null;
};

export type BulkCreateActionLibraryInstanceDTO = {
  asset_ids: number[];
  template_id: number | null;
  time_parameters: BulkCreateActionLibraryInstanceTimeParametersDTO;
  adhoc_data?: BulkCreateActionLibraryInstanceAdhocDataDTO;
  instance_name?: string | null;
  report_to?: string | null;
  special_instruction?: string | null;
  template_step_overrides?: TemplateStepOverrideDTO[];
  validity_period_months?: number | null;
  criticality?: ActionLibraryInstanceCriticality;
};

// Frontend Type (camelCase)
export type ActionLibraryInstanceStep = {
  id: number;
  title: string;
  instruction: string;
  mandatory: boolean;
  order: number;
  isCompleted: boolean;
  completedAt: string | null;
  completedBy: number | null;
  status?: ActionLibraryInstanceStatus | ActionLibraryInstanceStepStatus;
  statusDisplay?: string;
  blockReason?: string;
  completionComment?: string;
  mandatoryTooltip?: string | null;
  reference?: string | null;
  relevantFor?: string | null;
  reportedTo?: string | null;
  tags?: Record<string, unknown> | null;
  assignedTo?: number | null;
  assignedToFullName?: string | null;
};

export type ActionLibraryInstance = {
  id: number;
  title: string;
  description: string;
  specialInstruction: string;
  reportedTo?: string;
  area?: number | null;
  areaDisplay?: string;
  areaData?: InstanceAreaData | null;
  subArea?: number | null;
  subAreaDisplay?: string;
  subAreaData?: InstanceSubAreaData | null;
  criticality: ActionLibraryInstanceCriticality;
  deadline: string;
  reminder: string | null;
  status: ActionLibraryInstanceStatus;
  statusDisplay?: string;
  statusPercent: number;
  organization: number;
  asset: number;
  assetName: string;
  assetType: string;
  assetImportance?: string | null;
  assetImportanceDisplay?: string | null;
  owner: number;
  ownerFullName: string;
  delegatedTo: number | null;
  delegatedToFullName: string;
  delegationComment?: string | null;
  templateId?: number | null;
  templateName?: string | null;
  templateReference?: string | null;
  templateSummary?: string | null;
  templateSource?: string | null;
  templateSourceDisplay?: string | null;
  templateTags?: Record<string, unknown> | null;
  validationPeriod?: string | null;
  /** Months from API `result_validity_period_months` only (outcome validity); not merged with template `validity_period_months`. */
  resultValidityPeriodMonths?: number | null;
  validityPeriodMonths?: number | null;
  validUntil?: string | null;
  isCompleted?: boolean;
  completedStepsCount?: number;
  totalStepsCount?: number;
  stepsCount?: number;
  canComplete?: boolean;
  completeButtonTooltip?: string | null;
  canReopen?: boolean;
  canDelegate: boolean;
  canDelete?: boolean;
  completionComment?: string | null;
  reference?: string | null;
  tags?: Record<string, unknown> | null;
  steps?: ActionLibraryInstanceStep[];
  instanceCommentsCount?: number;
  lastInstanceComment?: InstanceComment | null;
  createdAt: string;
  updatedAt: string;
};

export type BulkCreateActionLibraryInstanceAdhocStep = {
  title: string;
  instruction: string;
  mandatory: boolean;
  order: number;
};

export type BulkCreateActionLibraryInstanceAdhocData = {
  title: string;
  description: string;
  specialInstruction: string;
  steps: BulkCreateActionLibraryInstanceAdhocStep[];
};

export type BulkCreateActionLibraryInstanceTimeParameters = {
  deadline: string;
  reminder?: string | null;
};

/** Per-step override when creating from template (FE camelCase). */
export type TemplateStepOverride = {
  stepId: number;
  mandatory?: boolean;
  relevantFor?: string | null;
  reference?: string | null;
  reportedTo?: string | null;
  tags?: string[] | null;
};

/** Per-step override when creating from template (BE snake_case). */
export type TemplateStepOverrideDTO = {
  step_id: number;
  mandatory?: boolean;
  relevant_for?: string | null;
  reference?: string | null;
  reported_to?: string | null;
  tags?: string[] | null;
};

export type BulkCreateActionLibraryInstancePayload = {
  assetIds: number[];
  templateId?: number | null;
  timeParameters: BulkCreateActionLibraryInstanceTimeParameters;
  adhocData?: BulkCreateActionLibraryInstanceAdhocData;
  instanceName?: string | null;
  reportedTo?: string | null;
  specialInstruction?: string | null;
  templateStepOverrides?: TemplateStepOverride[];
  validityPeriodMonths?: number | null;
  criticality?: ActionLibraryInstanceCriticality;
};

/** Response shape from createBulk – includes status and headers for X-Skipped-Assets handling. */
export type CreateBulkActionLibraryInstancesResponse = {
  data: unknown;
  status: number;
  headers: Record<string, string>;
};

/** Payload for POST create-bulk-precheck (read-only; which assets already have active instances). */
export type BulkCreatePrecheckPayload = {
  templateId: number;
  assetIds: number[];
};

/** Response from create-bulk-precheck (200). */
export type BulkCreatePrecheckResponse = {
  templateId: number;
  assetIds: number[];
  alreadyActiveAssetIds: number[];
};

export type UpdateActionLibraryInstancePayload = {
  title: string;
  deadline: string;
  reminder?: string | null;
  delegatedTo: number | null;
  reportedTo?: string | null;
  specialInstruction: string;
  steps: {
    id: number;
    mandatory: boolean;
    status?: ActionLibraryInstanceStepStatus;
  }[];
  assetId: number;
  status?: ActionLibraryInstanceStatus;
  completionComment?: string;
  criticality?: ActionLibraryInstanceCriticality;
  validityPeriodMonths?: number | null;
};

/** Payload for delegation-only PATCH (AssetActionDelegate). */
export type DelegateActionLibraryInstancePayload = {
  delegatedTo: number | null;
  delegationComment: string;
};

/** Payload for POST asset-actions/{id}/complete/ (AssetActionComplete). */
export type CompleteActionLibraryInstancePayload = {
  completionComment?: string;
};

/** Payload for POST asset-actions/{id}/reopen/ (AssetActionReopen). Comment is required. */
export type ReopenInstancePayload = {
  comment: string;
};

// --- Instance Activity Log (GET /api/v1/asset-actions/{id}/status-logs/) ---
// Backend may return paginated list of status/activity entries (delegation, completion, etc.)
export type InstanceActivityLogEntryDTO = {
  id: number;
  user: number | null;
  user_email: string;
  user_full_name: string;
  message?: string;
  created_at: string;
};

export type InstanceActivityLogEntry = {
  id: number;
  user: number | null;
  userEmail: string;
  userFullName: string;
  message?: string;
  createdAt: string;
};

export type InstanceActivityLogListParams = {
  limit?: number;
  offset?: number;
};

export type PaginatedInstanceActivityLogResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: InstanceActivityLogEntry[];
};

// --- Instance Comments (GET/POST /api/v1/asset-actions/{id}/comments/) UC34 ---
/** Comment purpose: 1 = General, 2 = Delegation, 3 = Completion */
export type InstanceCommentPurpose = 1 | 2 | 3;

export type InstanceCommentDTO = {
  id: number;
  text: string;
  text_preview?: string;
  is_truncated?: boolean | string;
  purpose?: InstanceCommentPurpose;
  purpose_display?: string;
  user_email: string;
  user_full_name: string;
  created_at: string;
  updated_at: string;
};

export type InstanceComment = {
  id: number;
  text: string;
  textPreview?: string;
  isTruncated?: boolean;
  purpose?: InstanceCommentPurpose;
  purposeDisplay?: string;
  userEmail: string;
  userFullName: string;
  createdAt: string;
  updatedAt: string;
};

export type InstanceCommentCreatePayload = {
  text: string;
};

export type InstanceCommentListParams = {
  limit?: number;
  offset?: number;
};

export type PaginatedInstanceCommentResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: InstanceComment[];
};

// Instance Dashboard Metrics (GET /api/v1/asset-actions/metrics/)
export type InstanceDashboardMetricsCreatedPeriod = {
  total: number;
  completed: number;
  not_started: number;
};

export type InstanceDashboardMetricsDTO = {
  percentage_completed_instances: number;
  active_instances_count: number;
  overdue_active_instances_count: number;
  created_last_30_days: Record<string, number>;
  created_last_90_days: Record<string, number>;
  created_last_180_days: Record<string, number>;
  created_last_270_days: Record<string, number>;
  created_last_365_days: Record<string, number>;
};

export type InstanceDashboardMetrics = {
  percentageCompletedInstances: number;
  activeInstancesCount: number;
  overdueActiveInstancesCount: number;
  createdLast30Days: InstanceDashboardMetricsCreatedPeriod;
  createdLast90Days: InstanceDashboardMetricsCreatedPeriod;
  createdLast180Days: InstanceDashboardMetricsCreatedPeriod;
  createdLast270Days: InstanceDashboardMetricsCreatedPeriod;
  createdLast365Days: InstanceDashboardMetricsCreatedPeriod;
};

// Query Params (names match API documentation)
export type ActionLibraryInstanceListParams = {
  area?: string;
  asset_id?: string;
  asset__type?: number;
  asset_importance?: string;
  criticality?: string;
  created_from?: string;
  created_to?: string;
  date_from?: string;
  date_to?: string;
  delegated_to?: string;
  due_date_up_to?: string;
  id?: number;
  limit?: number;
  offset?: number;
  ordering?: string;
  owner?: string;
  past_due_date?: boolean;
  past_reminder_date?: boolean;
  responsible_user?: string;
  search?: string;
  source?: string;
  status?: string;
  steps_count?: number;
  sub_area?: number;
  tags?: string;
  template?: string;
  template_name?: string;
  validity?: string;
};

/** UC19 – GET /api/v1/asset-actions/export/ */
export type AssetActionInstanceExportFormat = 'json' | 'csv';

export const ASSET_ACTION_INSTANCE_EXPORT_DEFAULT_FILENAMES: Record<AssetActionInstanceExportFormat, string> = {
  csv: 'asset-actions-export.csv',
  json: 'asset-actions-export.json',
};

/** Same filter/search keys as the instance list; pagination must not be sent (export ignores it). */
export type ActionLibraryInstanceExportParams = Omit<ActionLibraryInstanceListParams, 'limit' | 'offset'> & {
  export_format: AssetActionInstanceExportFormat;
};

export type ActionLibraryInstanceExportResponse = {
  blob: Blob;
  contentType: string | null;
  filename: string;
};

// API Interface
export interface ActionLibraryInstanceAPI {
  getAllActionLibraryInstances: (params?: ActionLibraryInstanceListParams) => Promise<{
    data: PaginatedResponse<ActionLibraryInstance>;
  }>;
  getPersonalActionLibraryInstances: (params?: ActionLibraryInstanceListParams) => Promise<{
    data: PaginatedResponse<ActionLibraryInstance>;
  }>;
  getSingleActionLibraryInstance: (id: number) => Promise<{ data: ActionLibraryInstance }>;
  deleteActionLibraryInstance: (id: number) => Promise<void>;
  createBulkActionLibraryInstances: (data: BulkCreateActionLibraryInstancePayload) => Promise<CreateBulkActionLibraryInstancesResponse>;
  createBulkPrecheck: (data: BulkCreatePrecheckPayload) => Promise<{ data: BulkCreatePrecheckResponse }>;
  updateActionLibraryInstance: (
    id: number,
    data: UpdateActionLibraryInstancePayload,
  ) => Promise<{
    data: ActionLibraryInstance;
  }>;
  delegateActionLibraryInstance: (
    id: number,
    data: DelegateActionLibraryInstancePayload,
  ) => Promise<{
    data: ActionLibraryInstance;
  }>;
  completeActionLibraryInstance: (
    id: number,
    data: CompleteActionLibraryInstancePayload,
  ) => Promise<{
    data: ActionLibraryInstance;
  }>;
  reopenActionLibraryInstance: (
    id: number,
    data: ReopenInstancePayload,
  ) => Promise<{
    data: ActionLibraryInstance;
  }>;
  getActionLibraryInstanceMetrics: (params?: ActionLibraryInstanceListParams) => Promise<{
    data: InstanceDashboardMetrics;
  }>;
  getInstanceStatusLogs: (id: number, params?: InstanceActivityLogListParams) => Promise<{ data: PaginatedInstanceActivityLogResponse }>;
  getInstanceComments: (id: number, params?: InstanceCommentListParams) => Promise<{ data: PaginatedInstanceCommentResponse }>;
  createInstanceComment: (id: number, payload: InstanceCommentCreatePayload) => Promise<{ data: InstanceComment }>;
  exportActionLibraryInstances: (params: ActionLibraryInstanceExportParams) => Promise<{ data: ActionLibraryInstanceExportResponse }>;
}
